import { useEffect, useMemo, useState } from 'react'
import {
  Button,
  Input,
  InputNumber,
  Message,
  Switch,
  Table,
  Tag,
  Typography,
} from '@arco-design/web-react'
import { IconDelete, IconPlus } from '@arco-design/web-react/icon'
import type { OptionsAPI } from '../useOptions'

/** 进价：每个模型从上游采购的单价（USD / 百万 token），与售价（ModelRatio）无关 */
interface ModelCost {
  InputPerMTokUsd?: number
  OutputPerMTokUsd?: number
  CacheReadPerMTokUsd?: number
  CacheWritePerMTokUsd?: number
  FixedPriceUsd?: number
}

type CostTable = Record<string, ModelCost>

const KEY_ENABLED = 'channel_cost_setting.Enabled'
const KEY_DEFAULT_RATIO = 'channel_cost_setting.DefaultCostRatio'
const KEY_TABLE = 'channel_cost_setting.UpstreamModelCost'

const parseCostTable = (raw: string): CostTable => {
  if (!raw) return {}
  try {
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? (parsed as CostTable) : {}
  } catch {
    return {}
  }
}

const fmtUsd = (v?: number) => (v === undefined || v === null || v === 0 ? '—' : `$${v}`)

/**
 * 渠道成本（进价）设置。
 *
 * 这里填的是「我们向上游付多少」，售价在「价格设置」里，两者之差就是毛利。
 * 没配进价的模型会按下方「默认成本比例 × 售价」估算，毛利数据只能参考。
 */
export default function ChannelCostSetting({ options }: { options: OptionsAPI }) {
  const enabled = options.getBool(KEY_ENABLED, true)
  const defaultRatio = options.getNumber(KEY_DEFAULT_RATIO, 0.7)
  // 依赖原始字符串而不是 options 对象：options 每次渲染都是新引用，
  // 用它做依赖会让下面的 effect 每次渲染都重置表格（输入被吃掉/死循环）。
  const rawTable = options.get(KEY_TABLE, '{}')
  const savedTable = useMemo(() => parseCostTable(rawTable), [rawTable])

  const [rows, setRows] = useState<Array<{ model: string } & ModelCost>>([])
  const [newModel, setNewModel] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setRows(
      Object.entries(savedTable)
        .map(([model, cost]) => ({ model, ...cost }))
        .sort((a, b) => a.model.localeCompare(b.model))
    )
  }, [savedTable])

  const updateRow = (model: string, patch: Partial<ModelCost>) => {
    setRows((prev) => prev.map((r) => (r.model === model ? { ...r, ...patch } : r)))
  }

  const addRow = () => {
    const name = newModel.trim()
    if (!name) return
    if (rows.some((r) => r.model === name)) {
      Message.warning('该模型已存在')
      return
    }
    setRows((prev) => [...prev, { model: name }].sort((a, b) => a.model.localeCompare(b.model)))
    setNewModel('')
  }

  const save = async () => {
    const table: CostTable = {}
    rows.forEach((r) => {
      const cost: ModelCost = {}
      if (r.InputPerMTokUsd) cost.InputPerMTokUsd = r.InputPerMTokUsd
      if (r.OutputPerMTokUsd) cost.OutputPerMTokUsd = r.OutputPerMTokUsd
      if (r.CacheReadPerMTokUsd) cost.CacheReadPerMTokUsd = r.CacheReadPerMTokUsd
      if (r.CacheWritePerMTokUsd) cost.CacheWritePerMTokUsd = r.CacheWritePerMTokUsd
      if (r.FixedPriceUsd) cost.FixedPriceUsd = r.FixedPriceUsd
      if (Object.keys(cost).length) table[r.model.trim()] = cost
    })
    setSaving(true)
    try {
      const result = await options.save([KEY_ENABLED, KEY_DEFAULT_RATIO, KEY_TABLE], {
        [KEY_ENABLED]: String(enabled),
        [KEY_DEFAULT_RATIO]: String(defaultRatio),
        [KEY_TABLE]: JSON.stringify(table),
      })
      if (result) Message.success('已保存进价配置')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="settings-risk-alert settings-info-strip">
        这里配置的是**进价**（我们付给上游的钱），售价在「价格设置」里。两者之差就是毛利。
        未列入下表的模型会按「默认成本比例 × 售价」估算，渠道页的毛利会标记为「估算」。
      </div>

      <div className="flex flex-wrap items-center gap-6">
        <div className="flex items-center gap-2">
          <span className="text-sm">启用成本台账</span>
          <Switch checked={enabled} onChange={(v) => options.patch({ [KEY_ENABLED]: String(v) })} />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm">默认成本比例</span>
          <InputNumber
            min={0}
            max={1}
            step={0.05}
            value={defaultRatio}
            onChange={(v) => options.patch({ [KEY_DEFAULT_RATIO]: String(v ?? 0.7) })}
            style={{ width: 120 }}
          />
          <Typography.Text className="text-xs text-[var(--console-text-muted)]">
            未配置进价时，成本 = 售价 × 该比例（默认 0.7，即假定毛利 30%）
          </Typography.Text>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Input
          value={newModel}
          onChange={setNewModel}
          onPressEnter={addRow}
          placeholder="要配置进价的模型名，例如 gpt-4o-mini"
          style={{ maxWidth: 360 }}
        />
        <Button icon={<IconPlus />} onClick={addRow}>
          添加模型
        </Button>
        <Tag color={rows.length ? 'green' : 'orange'}>
          {rows.length ? `${rows.length} 个模型有真实进价` : '尚未配置任何进价，全部按估算'}
        </Tag>
      </div>

      <Table
        borderCell
        pagination={false}
        data={rows}
        rowKey="model"
        noDataElement="还没有配置进价。没有进价时，渠道页只能显示「估算毛利」。"
        columns={[
          { title: '模型', dataIndex: 'model', width: 200 },
          {
            title: '输入 $/M tok',
            width: 130,
            render: (_: unknown, r: { model: string } & ModelCost) => (
              <InputNumber
                min={0}
                step={0.01}
                value={r.InputPerMTokUsd}
                onChange={(v) => updateRow(r.model, { InputPerMTokUsd: v ?? undefined })}
              />
            ),
          },
          {
            title: '输出 $/M tok',
            width: 130,
            render: (_: unknown, r: { model: string } & ModelCost) => (
              <InputNumber
                min={0}
                step={0.01}
                value={r.OutputPerMTokUsd}
                onChange={(v) => updateRow(r.model, { OutputPerMTokUsd: v ?? undefined })}
              />
            ),
          },
          {
            title: '缓存读 $/M',
            width: 120,
            render: (_: unknown, r: { model: string } & ModelCost) => (
              <InputNumber
                min={0}
                step={0.005}
                value={r.CacheReadPerMTokUsd}
                onChange={(v) => updateRow(r.model, { CacheReadPerMTokUsd: v ?? undefined })}
              />
            ),
          },
          {
            title: '缓存写 $/M',
            width: 120,
            render: (_: unknown, r: { model: string } & ModelCost) => (
              <InputNumber
                min={0}
                step={0.005}
                value={r.CacheWritePerMTokUsd}
                onChange={(v) => updateRow(r.model, { CacheWritePerMTokUsd: v ?? undefined })}
              />
            ),
          },
          {
            title: '按次固定价 $',
            width: 130,
            render: (_: unknown, r: { model: string } & ModelCost) => (
              <InputNumber
                min={0}
                step={0.001}
                value={r.FixedPriceUsd}
                onChange={(v) => updateRow(r.model, { FixedPriceUsd: v ?? undefined })}
              />
            ),
          },
          {
            title: '摘要',
            render: (_: unknown, r: { model: string } & ModelCost) =>
              r.FixedPriceUsd
                ? `按次 ${fmtUsd(r.FixedPriceUsd)}`
                : `in ${fmtUsd(r.InputPerMTokUsd)} / out ${fmtUsd(r.OutputPerMTokUsd)}`,
          },
          {
            title: '',
            width: 60,
            render: (_: unknown, r: { model: string }) => (
              <Button
                size="mini"
                status="danger"
                icon={<IconDelete />}
                onClick={() => setRows((prev) => prev.filter((x) => x.model !== r.model))}
              />
            ),
          },
        ]}
      />

      <div>
        <Button type="primary" loading={saving} onClick={save}>
          保存进价配置
        </Button>
      </div>
    </div>
  )
}
